// #86 Assignment: the Recruiter assignment card on the request (DC-70). The recommended Recruiter (Person), the
// *Workload* and *Expertise* bars, the status pill of the chain *Recommended → Pending TA Manager → Assigned*, and the
// actions the viewing seat holds: *Assign* (TA Manager, the one primary), *Reassign* (his Step 2 verb, "Create/post ·
// Assign · Reassign · Request reassignment": another Recruiter from the same ranked list; the `chooseAnother` prop keeps
// its name) and *Request reassignment* (Recruiter, HR Business Partner). There is no Claim anywhere. An action the seat
// does not hold is absent (omit it); one a rule blocks is dashed with his sentence (DC-24). `refused`: the red banner at
// the top of the card, the entered values kept. mode="view": the person, bars and status, no actions. `chooser` is the
// slot *Reassign* opens (the caller's PersonPick): the region it fills stays in the DOM whenever *Reassign* is drawn,
// hidden while collapsed, so the button always controls it and is expanded while a chooser is given (B2-3).
// `reassignment` draws the *Reassignment request* row (G §5): who asked and when (a <time>), with its status pill.

import { useId, type ReactNode } from "react";
import { Bars, Button, Card, Person, Pill, type Blocked, type CardRefused } from "../core";
import { ACTION_LABELS, RECRUITER_ASSIGNMENT_STATUS, ROLE_LABELS } from "../labels";
import { SURFACE_LABELS } from "../core";
import { G6_LABELS } from "./labels";
import type { Hs } from "../lib/hs";
import { formatDate, toEpoch, type Instant } from "../lib/time";

export type AssignmentStatus = keyof typeof RECRUITER_ASSIGNMENT_STATUS;

/** An action the seat holds: present when given; `blocked` dashes it with his rule sentence. */
export interface AssignmentAction {
  onClick?: (() => void) | undefined;
  blocked?: Blocked | undefined;
  busy?: boolean | undefined;
  hs?: Hs | undefined;
}

export interface AssignmentProps {
  /** The recommended (or assigned) Recruiter. */
  person: { name: string; sub?: ReactNode | undefined; hueKey?: string | undefined };
  /** 0–100, with the words or number shown beside each bar (default the percentage). */
  workload: number;
  workloadValue?: ReactNode | undefined;
  expertise: number;
  expertiseValue?: ReactNode | undefined;
  status: AssignmentStatus;
  /** The viewing seat's actions; an absent key is an absent control. */
  assign?: AssignmentAction | undefined;
  /** *Reassign*: another Recruiter from the ranked list, through the chooser it opens. */
  chooseAnother?: AssignmentAction | undefined;
  requestReassignment?: AssignmentAction | undefined;
  /** The chooser *Reassign* opens (a PersonPick); drawn under the bars while given, in edit mode. */
  chooser?: ReactNode | undefined;
  /** A Reassignment request on this assignment: who asked, when, and its status pill. */
  reassignment?: { by: string; at: Instant; status?: ReactNode | undefined } | undefined;
  refused?: CardRefused | undefined;
  mode?: "edit" | "view" | undefined;
  can?: boolean | undefined;
  className?: string | undefined;
}

export function Assignment({
  person, workload, workloadValue, expertise, expertiseValue, status, assign, chooseAnother, requestReassignment, chooser, reassignment, refused, mode,
  can, className,
}: AssignmentProps) {
  const chooserId = useId();
  if (can === false) return null;
  const view = mode === "view";
  const choosing = !view && chooser !== undefined && chooser !== null && chooser !== false;
  const actions = view ? null : (
    <>
      {assign ? (
        <Button tone="primary" sm onClick={assign.onClick} blocked={assign.blocked} busy={assign.busy} hs={assign.hs}>{ACTION_LABELS.assign}</Button>
      ) : null}
      {chooseAnother ? (
        <Button tone="ghost" sm onClick={chooseAnother.onClick} blocked={chooseAnother.blocked} busy={chooseAnother.busy} hs={chooseAnother.hs}
          aria-expanded={choosing} aria-controls={chooserId}>
          {ACTION_LABELS.reassign}
        </Button>
      ) : null}
      {requestReassignment ? (
        <Button tone="ghost" sm onClick={requestReassignment.onClick} blocked={requestReassignment.blocked} busy={requestReassignment.busy} hs={requestReassignment.hs}>
          {ACTION_LABELS.requestReassignment}
        </Button>
      ) : null}
    </>
  );
  const hasActions = !view && Boolean(assign || chooseAnother || requestReassignment);
  return (
    <Card
      title={ROLE_LABELS.recruiter}
      pills={<Pill>{RECRUITER_ASSIGNMENT_STATUS[status]}</Pill>}
      refused={refused}
      className={className ? `mc-asg ${className}` : "mc-asg"}
    >
      <div className="mc-asg-b">
        <Person name={person.name} sub={person.sub} hueKey={person.hueKey} size={36} />
        <Bars
          items={[
            { label: SURFACE_LABELS.workload, pct: workload, value: workloadValue },
            { label: SURFACE_LABELS.expertise, pct: expertise, value: expertiseValue },
          ]}
        />
      </div>
      {!view && chooseAnother ? <div className="mc-asg-c" id={chooserId} hidden={!choosing}>{choosing ? chooser : null}</div> : null}
      {reassignment ? (
        <div className="mc-asg-r">
          <b>{G6_LABELS.reassignmentRequest}</b>
          <span>
            {`${reassignment.by} · `}<time dateTime={new Date(toEpoch(reassignment.at)).toISOString()}>{formatDate(reassignment.at)}</time>
          </span>
          {reassignment.status ?? null}
        </div>
      ) : null}
      {hasActions ? <div className="mc-asg-a">{actions}</div> : null}
    </Card>
  );
}
